import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { TextButton } from '@creditornot/cb-button';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

const StyledMenuItemGroup = styled(MenuItemGroup)`
  margin: 8px 16px;
`;

export type SortOption<
  SortBy extends string | undefined,
  SortingType extends string | undefined,
> = {
  label: string;
  sortBy: Exclude<SortBy, undefined>;
  sortingType: Exclude<SortingType, undefined>;
};

type Props<SortBy extends string | undefined, SortingType extends string | undefined> = {
  sortBy: SortBy;
  sortingType?: SortingType;
  options: SortOption<SortBy, SortingType>[];
  onSortChange: (sortBy: SortBy, sortingType: SortingType) => void;
};

export function SortButton<
  SortBy extends string | undefined,
  SortingType extends string | undefined,
>({ sortBy, sortingType, options, onSortChange }: Props<SortBy, SortingType>) {
  const format = useFormatMessage();

  const currentOption = options.find((option) => {
    return option.sortBy === sortBy && (!sortingType || option.sortingType === sortingType);
  });

  return (
    <Popover
      offset={4}
      placement="bottom-start"
      renderArrow={false}
      content={({ close }) => (
        <StyledMenuItemGroup data-test-id="sort-popover">
          {options.map((option) => (
            <MenuActionItem
              variant="black"
              selected={option === currentOption}
              key={`${option.sortBy}${option.sortingType}`}
              onClick={() => {
                onSortChange(option.sortBy, option.sortingType);
                close();
              }}
              data-test-id={`sort-${option.sortBy.toLowerCase()}-${option.sortingType ? `${option.sortingType.toLowerCase()}-` : ''}radio`}
            >
              {option.label}
            </MenuActionItem>
          ))}
        </StyledMenuItemGroup>
      )}
    >
      {({ refProps }) => (
        <TextButton
          data-test-id="sort-button"
          variant="neutral"
          icon={icons.arrowUpDown}
          iconPosition="end"
          {...refProps}
        >
          {currentOption?.label ?? format('common.sort')}
        </TextButton>
      )}
    </Popover>
  );
}
